/**
 * useUsers Hook
 * Standardized API hooks for user management operations
 *
 * Refactored: Phase 3.5 - Factory Pattern Migration
 * Pattern: Uses createQueryHook and createMutationHook factories for code reduction
 * Previous: Manual useQuery/useMutation with helper functions
 * Current: Factory-based hooks with built-in logging and invalidation
 *
 * Migration Benefits:
 * - Reduced code duplication
 * - Standardized logging patterns
 * - Centralized query invalidation
 * - Type-safe configuration
 */

import { UserCreate, UserUpdate, User, PasswordChangeRequest, StatusResponse, UserListResponse } from '../../api';
import { log } from '../../utils/errors/logger';
import { QUERY_STALE_TIME } from '../../constants/queryConfig';
import { createQueryHookNoParams, createMutationHook } from './factories';

// ============================================================================
// Query Keys
// ============================================================================

export const userQueryKeys = {
  all: ['users'] as const,
  lists: () => [...userQueryKeys.all, 'list'] as const,
  details: () => [...userQueryKeys.all, 'detail'] as const,
  detail: (username: string) => [...userQueryKeys.details(), username] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to get paginated users with optional search.
 * Single-tenant system - no organization filtering needed.
 *
 * Features:
 * - Server-side pagination with skip/limit
 * - Optional search query (searches username, email, full_name)
 * - Short stale time (30s) as user data changes frequently
 * - Automatic request/response/error logging via logContext
 *
 * Usage:
 * @example
 * // Basic usage - get first page
 * const { data } = useUsers({ skip: 0, limit: 10 });
 * const users = data?.users || [];
 * const total = data?.total || 0;
 *
 * // With search
 * const { data } = useUsers({ skip: 0, limit: 10, search: 'john' });
 *
 * // With enabled option
 * const { data } = useUsers({ skip: 0, limit: 10 }, { enabled: open });
 */
export const useUsers = (params?: { skip?: number; limit?: number; search?: string }, options?: any) => {
  const skip = params?.skip ?? 0;
  const limit = params?.limit ?? 20;
  const search = params?.search;

  return createQueryHookNoParams<UserListResponse>({
    queryKey: () => [...userQueryKeys.lists(), skip, limit, search],
    queryFn: (apiClient) =>
      apiClient.getUsers({ skip, limit, search }).then(response => response.data),
    logContext: 'GET /api/users',
    staleTime: QUERY_STALE_TIME.SHORT, // User data changes frequently
  })(options);
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to create a new user.
 */
export const useCreateUser = createMutationHook<User, UserCreate>({
  mutationFn: (apiClient, newUserData) =>
    apiClient.createUser(newUserData).then(response => response.data),
  invalidateQueries: [
    userQueryKeys.all,
  ],
  logContext: 'POST /api/users',
  logMethod: 'POST',
  onSuccess: (user) => {
    log.user.action('user_created', 'useCreateUser', { username: user.username });
  },
});

/**
 * Hook to update a user.
 */
export const useUpdateUser = createMutationHook<User, { username: string; data: UserUpdate }>({
  mutationFn: (apiClient, { username, data }) =>
    apiClient.updateUser(username, data).then(response => response.data),
  invalidateQueries: [
    userQueryKeys.all,
  ],
  logContext: 'PUT /api/users/:username',
  logMethod: 'PUT',
  onSuccess: (user, variables) => {
    log.user.action('user_updated', 'useUpdateUser', { username: variables.username });
  },
});

/**
 * Hook to delete a user.
 */
export const useDeleteUser = createMutationHook<StatusResponse, string>({
  mutationFn: (apiClient, username) =>
    apiClient.deleteUser(username).then(response => response.data),
  invalidateQueries: [
    userQueryKeys.all,
  ],
  logContext: 'DELETE /api/users/:username',
  logMethod: 'DELETE',
  onSuccess: (_data, username) => {
    log.user.action('user_deleted', 'useDeleteUser', { username });
  },
});

/**
 * Hook to change own password with current password verification.
 *
 * Security: Requires current password to prevent unauthorized password changes
 * if an attacker gains temporary access to an authenticated session.
 *
 * Usage:
 * @example
 * const changePassword = useChangePassword();
 *
 * await changePassword.mutateAsync({
 *   current_password: 'oldpass123',
 *   new_password: 'newpass123'
 * });
 */
export const useChangePassword = createMutationHook<
  StatusResponse,
  PasswordChangeRequest
>({
  mutationFn: (apiClient, data) =>
    apiClient.changeOwnPassword(data).then(response => response.data),
  logContext: 'POST /api/users/me/change-password',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('password_changed', 'useChangePassword', {});
  },
});

/**
 * Hook to request deletion of own account.
 * Starts a 7-day grace period during which the user can cancel the deletion.
 *
 * Usage:
 * @example
 * const requestDeletion = useRequestDeletion();
 *
 * await requestDeletion.mutateAsync();
 */
export const useRequestDeletion = createMutationHook<StatusResponse, void>({
  mutationFn: (apiClient) =>
    apiClient.requestOwnDeletion().then(response => response.data),
  invalidateQueries: [
    userQueryKeys.all,
    ['currentUser'], // Invalidate current user to reflect deletion status
  ],
  logContext: 'POST /api/users/me/request-deletion',
  logMethod: 'POST',
  onSuccess: () => {
    log.user.action('deletion_requested', 'useRequestDeletion', {});
  },
});

/**
 * Hook to cancel a pending deletion request.
 * Users can cancel their own deletion. Admins can cancel any deletion.
 *
 * Usage:
 * @example
 * const cancelDeletion = useCancelDeletion();
 *
 * await cancelDeletion.mutateAsync('username');
 */
export const useCancelDeletion = createMutationHook<StatusResponse, string>({
  mutationFn: (apiClient, username) =>
    apiClient.cancelDeletion(username).then(response => response.data),
  invalidateQueries: [
    userQueryKeys.all,
    ['currentUser'], // Invalidate current user if canceling own deletion
  ],
  logContext: 'POST /api/users/:username/cancel-deletion',
  logMethod: 'POST',
  onSuccess: (_data, username) => {
    log.user.action('deletion_cancelled', 'useCancelDeletion', { username });
  },
});

